import { useEffect, useState } from "react";
import api, { ASSET_URL } from "@/lib/api";
import AdminNav from "@/components/AdminNav";

const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = () => {
    api
      .get("/admin/orders")
      .then((res) => setOrders(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const update = async (id, patch) => {
    const res = await api.patch(`/admin/orders/${id}`, patch);
    setOrders((prev) => prev.map((o) => (o.id === id ? res.data : o)));
  };

  const shown = orders.filter((o) =>
    (o.event_name || "").toLowerCase().includes(filter.toLowerCase()),
  );

  if (loading) return <p>Loading…</p>;

  return (
    <div>
      <AdminNav />
      <div className="mb-5 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold">Orders</h1>
        <input
          className="input w-64"
          placeholder="Filter by event name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="space-y-3">
        {shown.map((o) => (
          <div key={o.id} className="card p-4">
            <div className="flex items-start gap-4">
              {/* Logo thumbnail — prominent per the B2B spec. */}
              {o.logo_filename ? (
                <img
                  src={`${ASSET_URL}/uploads/${o.logo_filename}`}
                  alt="Logo"
                  className="h-20 w-20 shrink-0 rounded border border-gray-200 object-contain p-1"
                />
              ) : (
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded border border-dashed border-gray-300 text-[10px] text-gray-400">
                  No logo
                </div>
              )}

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold">{o.order_number}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      o.payment_status === "paid"
                        ? "bg-fairway-100 text-fairway-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {o.payment_status}
                  </span>
                </div>
                {/* Event name — prominent. */}
                <p className="text-lg font-extrabold text-fairway-700">
                  {o.event_name || "(no event name)"}
                </p>
                <p className="text-sm text-gray-600">
                  {o.company_name} · {o.contact_name} · {o.email}
                </p>
                {o.logo_text && (
                  <p className="text-sm text-gray-500">
                    Logo text: <strong>{o.logo_text}</strong>
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  {o.items?.map((i) => `${i.name} ×${i.quantity}`).join(", ")} —
                  <strong> ${o.total?.toFixed(2)}</strong>
                </p>
                {o.order_notes && (
                  <p className="mt-1 text-sm italic text-gray-500">
                    "{o.order_notes}"
                  </p>
                )}
              </div>

              <div className="flex w-48 shrink-0 flex-col gap-2">
                <select
                  className="input"
                  value={o.status}
                  onChange={(e) => update(o.id, { status: e.target.value })}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <input
                  className="input"
                  placeholder="Tracking #"
                  defaultValue={o.tracking_number || ""}
                  onBlur={(e) =>
                    e.target.value !== (o.tracking_number || "") &&
                    update(o.id, { tracking_number: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        ))}
        {shown.length === 0 && (
          <p className="text-gray-500">No orders match.</p>
        )}
      </div>
    </div>
  );
}
